import React, { useState, useEffect } from 'react';
import logo from './logo.png';
import dashboardLogoBlack from './dashboardLogo_black.svg';
import dashboardLogoBlue from './dashboardLogo_blue.svg';
import firmwareLogoBlack from './firmwareLogo_black.svg';
import firmwareLogoBlue from './firmwareLogo_blue.svg';
import { Overview } from './overview.js';
import { Monitor } from './monitor.js';
import { SmartCapFirmware } from './smartCapFirmware.js';
import { login, logout, validateToken } from './LoginLogoutHandle';
import './App.css';

function Login({ onLogin }) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    
    try {
      const token = await login(username, password);
      if (token) {
        onLogin();
      }
    } catch (err) {
      setError(err.message || 'Login failed');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="login-container">
      <div className="login-box">
        <h2>P360 Smart Cap Dashboard</h2>
        <form onSubmit={handleSubmit}>
          <div className="login-field">
            <label>Username</label>
            <input
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="Enter username"
              required
            />
          </div>
          <div className="login-field">
            <label>Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter password"
              required
            />
          </div>
          {error && <div className="login-error">{error}</div>}
          <button 
            type="submit" 
            className="login-button"
            disabled={isLoading}
          >
            {isLoading ? 'Logging in...' : 'Login'}
          </button>
        </form>
      </div>
    </div>
  );
}

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isValidatingToken, setIsValidatingToken] = useState(true);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [activeSection, setActiveSection] = useState('overview');
  const [isDashboardOpen, setIsDashboardOpen] = useState(true);
  const [hoveredItem, setHoveredItem] = useState(null);
  const isDashboardActive = activeSection === 'overview' || activeSection === 'monitor';

  // Track user activity
  useEffect(() => {
    const updateActivity = () => {
      if (isAuthenticated) {
        localStorage.setItem('lastActivity', Date.now());
      }
    };

    window.addEventListener('click', updateActivity);
    window.addEventListener('keypress', updateActivity);
    window.addEventListener('scroll', updateActivity);
    window.addEventListener('mousemove', updateActivity);

    return () => {
      window.removeEventListener('click', updateActivity);
      window.removeEventListener('keypress', updateActivity);
      window.removeEventListener('scroll', updateActivity);
      window.removeEventListener('mousemove', updateActivity);
    };
  }, [isAuthenticated]);

  // Initial auth check
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const isValid = await validateToken();
        setIsAuthenticated(isValid);
      } catch (error) {
        console.error('Auth check failed:', error);
        setIsAuthenticated(false);
      } finally {
        setIsValidatingToken(false);
      }
    };

    checkAuth();
  }, []);

  // Periodic token check
  useEffect(() => {
    const interval = setInterval(async () => {
      if (isAuthenticated) {
        try {
          const isValid = await validateToken();
          if (!isValid) {
            await handleLogout();
          }
        } catch (error) {
          console.error('Token check error:', error);
        }
      }
    }, 5 * 60 * 1000); // Check every 5 minutes

    return () => clearInterval(interval);
  }, [isAuthenticated]);

  const handleLogin = () => {
    setIsAuthenticated(true);
  };

  const handleLogout = async () => {
    setIsLoggingOut(true);
    try {
      await logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setIsAuthenticated(false);
      setActiveSection('overview');
      setIsDashboardOpen(true);
      setIsLoggingOut(false);
    }
  };

  if (isValidatingToken) {
    return <div className="loading-screen">Loading...</div>;
  }

  if (!isAuthenticated) {
    return <Login onLogin={handleLogin} />;
  }
  
  return (
    <div className="App">
      <div className="header">
        <div className="header-content">
          <img src={logo} alt="logo" />
          <span>P360 Smart Cap Dashboard</span>
        </div>
        <button 
          onClick={handleLogout} 
          className="logout-button"
          disabled={isLoggingOut}
        >
          {isLoggingOut ? 'Logging out...' : 'Logout'}
        </button>
      </div>
      <div className="main">
        <div className="sidebar">
          <div
            onClick={() => setIsDashboardOpen(!isDashboardOpen)}
            style={{ color: isDashboardActive ? '#1890ff' : 'inherit' }}
          >
            <img 
              src={isDashboardActive ? dashboardLogoBlue : dashboardLogoBlack} 
              alt="dashboard logo" 
            />
            Dashboard {isDashboardOpen ? '↴' : '→'}
          </div>
          {isDashboardOpen && (
            <>
              <div
                onClick={() => setActiveSection('overview')}
                className={activeSection === 'overview' ? 'active' : ''}
                style={{ paddingLeft: '40px' }}
                onMouseEnter={() => setHoveredItem('overview')}
                onMouseLeave={() => setHoveredItem(null)}
              >
                Overview
                {hoveredItem === 'overview' && (
                  <span style={{ marginLeft: 'auto', color: '#1890ff' }}>→</span>
                )}
              </div>    
              <div
                onClick={() => setActiveSection('monitor')}
                className={activeSection === 'monitor' ? 'active' : ''}
                style={{ paddingLeft: '40px' }}
                onMouseEnter={() => setHoveredItem('monitor')}
                onMouseLeave={() => setHoveredItem(null)}
              >
                Monitor
                {hoveredItem === 'monitor' && (
                  <span style={{ marginLeft: 'auto', color: '#1890ff' }}>→</span>
                )}
              </div>
            </>
          )}
          <div
            onClick={() => setActiveSection('firmware')}
            className={activeSection === 'firmware' ? 'active' : ''}
            onMouseEnter={() => setHoveredItem('firmware')}
            onMouseLeave={() => setHoveredItem(null)}
          >
            <img src={activeSection === 'firmware' ? firmwareLogoBlue : firmwareLogoBlack} alt="firmware logo" />
            Smart Cap Firmware
            {hoveredItem === 'firmware' && (
              <span style={{ marginLeft: 'auto', color: '#1890ff' }}>→</span>
            )}
          </div>
        </div>
        <div className="content">
          {activeSection === 'overview' && <Overview />}
          {activeSection === 'monitor' && <Monitor />}
          {activeSection === 'firmware' && <SmartCapFirmware />}
        </div>
      </div>
    </div>
  );
}

export default App;